/**
 * From an operator's intent (a key press, `hotkeys.ts`) to the command it
 * sends (PLAN §5.3) — or to nothing, when the command could not be accepted
 * anyway: no take while the take guard refuses, no next or adlib command
 * while the playlist is inactive or the user may not take. The production is
 * the authority either way; this keeps a key press from sending what would
 * only come back nacked.
 */
import type { PlayoutCommandWire } from '@acp/api-contract/playout.js';
import type { AdlibItemView, AdlibPanelView } from './adlib.js';
import type { Intent } from './hotkeys.js';

/** A command without its id: the session mints the id. */
export type CommandInput = PlayoutCommandWire extends infer C ? (C extends PlayoutCommandWire ? Omit<C, 'commandId'> : never) : never;

export type IntentOutcome = { readonly kind: 'command'; readonly command: CommandInput } | { readonly kind: 'select'; readonly partId: string } | { readonly kind: 'none' };

export type IntentContext = {
  /** The take guard allows a take now. */
  readonly takeAllowed: boolean;
  /** Next and adlib commands may be sent: an active playlist and `playout.take`. */
  readonly canCommand: boolean;
  readonly panels: readonly AdlibPanelView[];
};

/** The command an adlib panel item sends; null when its piece type or action is not in the snapshot. */
export function itemCommand(item: AdlibItemView): CommandInput | null {
  if (item.missing) return null;
  if (item.kind === 'action') return { type: 'executeAction', actionId: item.actionId };
  return { type: 'adlib', pieceType: item.pieceType, params: { ...item.params }, mode: item.mode };
}

function command(ctx: IntentContext, c: CommandInput): IntentOutcome {
  return ctx.canCommand ? { kind: 'command', command: c } : { kind: 'none' };
}

export function intentOutcome(intent: Intent, ctx: IntentContext): IntentOutcome {
  switch (intent.type) {
    case 'take':
      return ctx.takeAllowed ? { kind: 'command', command: { type: 'take' } } : { kind: 'none' };
    case 'select':
      return { kind: 'select', partId: intent.partId };
    case 'moveNext':
      return command(ctx, { type: 'moveNext', delta: intent.delta });
    case 'setNext':
      return command(ctx, { type: 'setNext', partId: intent.partId });
    case 'setNextSegment':
      return command(ctx, { type: 'setNextSegment', segmentId: intent.segmentId });
    case 'queueNextSegment':
      return command(ctx, { type: 'queueNextSegment', segmentId: intent.segmentId });
    case 'item': {
      const item = ctx.panels.find((p) => p.id === intent.target.panelId)?.items[intent.target.index];
      const c = item === undefined ? null : itemCommand(item);
      return c === null ? { kind: 'none' } : command(ctx, c);
    }
  }
}
